import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

export type QuestionnaireList = {
  id: string;
  name: string;
  sort_order: number;
  templateIds: string[];
};

const favoritesKey = (clinicId: string | null | undefined, userId: string | null | undefined) =>
  ['questionnaire-favorites', clinicId ?? null, userId ?? null] as const;

const listsKey = (clinicId: string | null | undefined, userId: string | null | undefined) =>
  ['questionnaire-lists', clinicId ?? null, userId ?? null] as const;

export function useQuestionnaireFavorites(clinicId: string | null | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: favoritesKey(clinicId, userId),
    enabled: Boolean(clinicId && userId),
    queryFn: async (): Promise<string[]> => {
      if (!clinicId || !userId) return [];
      const { data, error } = await supabase
        .from('user_questionnaire_favorites')
        .select('forms_template_id')
        .eq('clinic_id', clinicId)
        .eq('user_id', userId);
      if (error) throw error;
      return (data ?? []).map((row) => row.forms_template_id);
    },
  });

  const toggle = useMutation({
    mutationFn: async (templateId: string) => {
      if (!clinicId || !userId) throw new Error('Not authenticated');
      const current = new Set(query.data ?? []);
      if (current.has(templateId)) {
        const { error } = await supabase
          .from('user_questionnaire_favorites')
          .delete()
          .eq('clinic_id', clinicId)
          .eq('user_id', userId)
          .eq('forms_template_id', templateId);
        if (error) throw error;
        return;
      }
      const { error } = await supabase.from('user_questionnaire_favorites').insert({
        clinic_id: clinicId,
        user_id: userId,
        forms_template_id: templateId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: favoritesKey(clinicId, userId) });
    },
  });

  return { ...query, toggle };
}

export function useQuestionnaireLists(clinicId: string | null | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: listsKey(clinicId, userId),
    enabled: Boolean(clinicId && userId),
    queryFn: async (): Promise<QuestionnaireList[]> => {
      if (!clinicId || !userId) return [];
      const { data: lists, error } = await supabase
        .from('user_questionnaire_lists')
        .select('id, name, sort_order')
        .eq('clinic_id', clinicId)
        .eq('user_id', userId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      const ids = (lists ?? []).map((row) => row.id);
      if (ids.length === 0) return [];
      const { data: items, error: itemsError } = await supabase
        .from('user_questionnaire_list_items')
        .select('list_id, forms_template_id, sort_order')
        .in('list_id', ids)
        .order('sort_order', { ascending: true });
      if (itemsError) throw itemsError;
      const byList = new Map<string, string[]>();
      for (const item of items ?? []) {
        const current = byList.get(item.list_id) ?? [];
        current.push(item.forms_template_id);
        byList.set(item.list_id, current);
      }
      return (lists ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        sort_order: row.sort_order,
        templateIds: byList.get(row.id) ?? [],
      }));
    },
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: listsKey(clinicId, userId) });
  }

  const createList = useMutation({
    mutationFn: async (input: { name: string; templateIds?: string[] }) => {
      if (!clinicId || !userId) throw new Error('Not authenticated');
      const nextOrder = (query.data?.length ?? 0) + 1;
      const { data, error } = await supabase
        .from('user_questionnaire_lists')
        .insert({
          clinic_id: clinicId,
          user_id: userId,
          name: input.name.trim() || 'Ny liste',
          sort_order: nextOrder,
        })
        .select('id')
        .single();
      if (error) throw error;
      const templateIds = input.templateIds ?? [];
      if (templateIds.length > 0) {
        const { error: itemError } = await supabase.from('user_questionnaire_list_items').insert(
          templateIds.map((id, index) => ({
            list_id: data.id,
            forms_template_id: id,
            sort_order: index,
          })),
        );
        if (itemError) throw itemError;
      }
      return data.id;
    },
    onSuccess: invalidate,
  });

  const renameList = useMutation({
    mutationFn: async (input: { listId: string; name: string }) => {
      const { error } = await supabase
        .from('user_questionnaire_lists')
        .update({ name: input.name.trim() })
        .eq('id', input.listId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteList = useMutation({
    mutationFn: async (listId: string) => {
      const { error } = await supabase.from('user_questionnaire_lists').delete().eq('id', listId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const replaceListItems = useMutation({
    mutationFn: async (input: { listId: string; templateIds: string[] }) => {
      const { error: delError } = await supabase
        .from('user_questionnaire_list_items')
        .delete()
        .eq('list_id', input.listId);
      if (delError) throw delError;
      if (input.templateIds.length === 0) return;
      const { error } = await supabase.from('user_questionnaire_list_items').insert(
        input.templateIds.map((id, index) => ({
          list_id: input.listId,
          forms_template_id: id,
          sort_order: index,
        })),
      );
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const reorderLists = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      for (const [index, id] of orderedIds.entries()) {
        const { error } = await supabase
          .from('user_questionnaire_lists')
          .update({ sort_order: index })
          .eq('id', id);
        if (error) throw error;
      }
    },
    onSuccess: invalidate,
  });

  return { ...query, createList, renameList, deleteList, replaceListItems, reorderLists };
}
